import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import inventoryCountService from '../../services/inventoryCountService';
import { productService } from '../../services/productService';
import Toast from '../../components/Common/Toast';

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('es-PE');
}

export default function InventoryCountWorkPanel({ warehouseId, campaignId, backTo }) {
  const [campaign, setCampaign] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [lineForm, setLineForm] = useState({ product_id: '', physical_qty: '', product_label: '' });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const load = useCallback(async () => {
    if (!warehouseId || !campaignId) return;
    setLoading(true);
    try {
      const res = await inventoryCountService.getChecklist(warehouseId, campaignId);
      if (res.success) {
        setChecklist(res.data);
        setCampaign(res.data?.campaign || null);
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'No se pudo cargar la campaña',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [warehouseId, campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const searchProducts = async () => {
    if (!productSearch.trim()) return;
    const res = await productService.searchForForm(productSearch.trim(), warehouseId);
    if (res.success) setProductResults(res.data || []);
  };

  const selectProduct = (product) => {
    setLineForm({
      product_id: String(product.id),
      physical_qty: '',
      product_label: `${product.sku || product.id} — ${product.name}`,
    });
    setProductResults([]);
    setProductSearch('');
  };

  const saveLine = async () => {
    if (!lineForm.product_id || lineForm.physical_qty === '') {
      setToast({ isOpen: true, message: 'Indique producto y cantidad física', type: 'warning' });
      return;
    }
    try {
      await inventoryCountService.saveLine(warehouseId, campaignId, {
        product_id: Number(lineForm.product_id),
        physical_qty: Number(lineForm.physical_qty),
        status: 'confirmed',
      });
      setToast({ isOpen: true, message: 'Conteo registrado', type: 'success' });
      setLineForm({ product_id: '', physical_qty: '', product_label: '' });
      load();
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al guardar conteo',
        type: 'error',
      });
    }
  };

  if (loading) {
    return <p className="text-gray-500 p-4">Cargando campaña…</p>;
  }

  if (!campaign) {
    return (
      <div className="p-4 bg-white border rounded-lg text-sm text-gray-600">
        No se encontró la campaña o no tiene permiso para verla.
        {backTo && (
          <Link to={backTo} className="block mt-2 text-teal-700 hover:underline">Volver</Link>
        )}
      </div>
    );
  }

  const progress = checklist?.progress;
  const items = checklist?.items || [];
  const lines = checklist?.lines || [];

  return (
    <div className="space-y-4">
      <div className="p-4 bg-white border rounded-lg">
        <h2 className="text-xl font-semibold text-gray-800">{campaign.name}</h2>
        <p className="text-sm text-gray-600 mt-1">
          {campaign.warehouse?.name || 'Almacén'} · {campaign.type === 'compra' ? 'Lista manual' : 'General'}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Límite: {formatDateTime(campaign.ends_at)}
        </p>
        {progress && (
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-1">Progreso: {progress.counted} / {progress.required}</p>
            <div className="h-2 bg-gray-200 rounded">
              <div
                className="h-2 bg-teal-600 rounded"
                style={{ width: `${Math.min(100, (progress.counted / Math.max(1, progress.required)) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {campaign.type === 'general' && (
        <section className="p-4 bg-white border rounded-lg text-sm">
          <h3 className="font-medium mb-2">Registrar producto contado</h3>
          <div className="flex flex-wrap gap-2 mb-2">
            <input
              className="border rounded px-2 py-2 flex-1 min-w-[200px]"
              placeholder="Buscar producto por SKU o nombre"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchProducts()}
            />
            <button type="button" onClick={searchProducts} className="px-3 py-2 bg-gray-100 rounded">
              Buscar
            </button>
          </div>
          {productResults.length > 0 && (
            <div className="max-h-40 overflow-y-auto border rounded mb-3">
              {productResults.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="block w-full text-left px-3 py-2 hover:bg-gray-50"
                  onClick={() => selectProduct(p)}
                >
                  {p.sku} — {p.name}
                </button>
              ))}
            </div>
          )}
          {lineForm.product_label && (
            <p className="text-gray-700 mb-2">Producto: <strong>{lineForm.product_label}</strong></p>
          )}
          <div className="flex flex-wrap gap-2">
            <input
              className="border rounded px-2 py-2 w-40"
              placeholder="Cantidad física"
              value={lineForm.physical_qty}
              onChange={(e) => setLineForm((f) => ({ ...f, physical_qty: e.target.value }))}
            />
            <button type="button" onClick={saveLine} className="px-4 py-2 bg-teal-600 text-white rounded">
              Guardar conteo
            </button>
          </div>
        </section>
      )}

      {campaign.type === 'compra' && items.length > 0 && (
        <section className="p-4 bg-white border rounded-lg">
          <h3 className="font-medium mb-3 text-sm">Productos de la campaña</h3>
          <ul className="text-sm divide-y">
            {items.map((item) => (
              <li key={item.product_id} className="py-2 flex justify-between items-center gap-2">
                <span>
                  {item.product?.name || item.product_id}
                  {item.counted ? ' ✓' : ''}
                </span>
                {!item.counted && (
                  <button
                    type="button"
                    className="text-teal-700 text-sm"
                    onClick={() => setLineForm({
                      product_id: String(item.product_id),
                      physical_qty: '',
                      product_label: item.product?.name || String(item.product_id),
                    })}
                  >
                    Contar
                  </button>
                )}
              </li>
            ))}
          </ul>
          {lineForm.product_id && (
            <div className="mt-4 flex flex-wrap gap-2 text-sm border-t pt-4">
              <span className="text-gray-700">{lineForm.product_label}</span>
              <input
                className="border rounded px-2 py-1 w-32"
                placeholder="Cantidad física"
                value={lineForm.physical_qty}
                onChange={(e) => setLineForm((f) => ({ ...f, physical_qty: e.target.value }))}
              />
              <button type="button" onClick={saveLine} className="px-3 py-1 bg-teal-600 text-white rounded">
                Guardar
              </button>
            </div>
          )}
        </section>
      )}

      {lines.length > 0 && (
        <section className="p-4 bg-white border rounded-lg">
          <h3 className="font-medium mb-2 text-sm">Conteos registrados</h3>
          <ul className="text-sm divide-y">
            {lines.map((line) => (
              <li key={line.id} className="py-2 flex justify-between">
                <span>{line.product?.name || line.product_id}</span>
                <span className="text-gray-600">Físico: {line.physical_qty} · Sistema: {line.system_qty}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {backTo && (
        <Link to={backTo} className="inline-block text-sm text-teal-700 hover:underline">
          ← Volver a mis campañas
        </Link>
      )}

      <Toast {...toast} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
}
